import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { StockLocation } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { countryName } from '@/features/markets';

export type LocationStatus = 'active' | 'inactive';

export const locationStatus = (location: StockLocation): LocationStatus =>
  location.isActive ? 'active' : 'inactive';

/** One line of the address, or undefined without one (sorts last). */
function addressLine({ address }: StockLocation): string | undefined {
  if (!address) return undefined;
  return [address.line1, address.city, address.region, countryName(address.countryCode)]
    .filter(Boolean)
    .join(', ');
}

/** Column model for the stock locations table. Default first, as the API returns them. */
export function useLocationColumns() {
  const { t } = useTranslation('inventory');

  return useMemo<Array<DataTableColumnDef<StockLocation>>>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('locations.columns.name'),
        meta: { sortField: 'name', lockVisibility: true },
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-2 font-medium">
            {row.original.name}
            {row.original.isDefault ? (
              <Badge variant="outline">{t('locations.default')}</Badge>
            ) : null}
            {row.original.isActive ? null : (
              <Badge variant="outline" className="text-muted-foreground">
                {t('locations.inactive')}
              </Badge>
            )}
          </span>
        ),
      },
      {
        id: 'address',
        accessorFn: addressLine,
        header: t('locations.columns.address'),
        sortUndefined: 'last',
        meta: { sortField: 'address' },
        cell: ({ row }) => {
          const line = addressLine(row.original);
          return line ? (
            <span className="text-sm">{line}</span>
          ) : (
            <span className="text-muted-foreground">{t('locations.noAddress')}</span>
          );
        },
      },
      {
        id: 'online',
        accessorKey: 'fulfillsOnlineOrders',
        header: t('locations.columns.online'),
        enableSorting: false,
        meta: { filterKey: 'online', searchable: false },
        cell: ({ row }) =>
          row.original.fulfillsOnlineOrders ? (
            <span className="text-sm">{t('locations.fulfils')}</span>
          ) : (
            <span className="text-muted-foreground text-sm">{t('locations.notFulfils')}</span>
          ),
      },
    ],
    [t],
  );
}
